import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services';

/**
 * Preferences Context
 * Manages user preferences like language, timezone, and date format across the application
 */

const PreferencesContext = createContext();

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};

// Language translations (comprehensive implementation)
const translations = {
  en: {
    // Common
    dashboard: 'Dashboard',
    sessions: 'Sessions',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading...',
    // Date-related
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    // Navigation
    organizations: 'Organizations',
    users: 'Users',
    analytics: 'Analytics',
    billing: 'Billing',
    waitlist: 'Waitlist',
    trainers: 'Trainers',
    reports: 'Reports',
    subscription: 'Subscription',
    mySessions: 'My Sessions',
    workbooks: 'Workbooks',
    terminology: 'Terminology',
    performance: 'Performance',
    help: 'Help',
    // Actions
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    view: 'View',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    // Status
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    completed: 'Completed',
    scheduled: 'Scheduled',
    // Dashboard cards
    totalSessions: 'Total Sessions',
    totalParticipants: 'Total Participants',
    avgEngagement: 'Avg Engagement',
    avgRating: 'Avg Rating',
    // Reports
    sessionReports: 'Session Reports',
    trainerReports: 'Trainer Reports',
  },
  es: {
    // Common
    dashboard: 'Panel',
    sessions: 'Sesiones',
    settings: 'Configuración',
    profile: 'Perfil',
    logout: 'Cerrar sesión',
    save: 'Guardar',
    cancel: 'Cancelar',
    loading: 'Cargando...',
    // Date-related
    today: 'Hoy',
    yesterday: 'Ayer',
    tomorrow: 'Mañana',
    // Navigation
    organizations: 'Organizaciones',
    users: 'Usuarios',
    analytics: 'Analítica',
    billing: 'Facturación',
    waitlist: 'Lista de espera',
    trainers: 'Formadores',
    reports: 'Informes',
    subscription: 'Suscripción',
    mySessions: 'Mis Sesiones',
    workbooks: 'Cuadernos',
    terminology: 'Terminología',
    performance: 'Rendimiento',
    help: 'Ayuda',
    // Actions
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    view: 'Ver',
    search: 'Buscar',
    filter: 'Filtrar',
    export: 'Exportar',
    import: 'Importar',
    // Status
    active: 'Activo',
    inactive: 'Inactivo',
    pending: 'Pendiente',
    completed: 'Completado',
    scheduled: 'Programado',
    // Dashboard cards
    totalSessions: 'Sesiones Totales',
    totalParticipants: 'Participantes Totales',
    avgEngagement: 'Compromiso Promedio',
    avgRating: 'Calificación Promedio',
    // Reports
    sessionReports: 'Informes de Sesión',
    trainerReports: 'Informes de Formadores',
  },
  fr: {
    // Common
    dashboard: 'Tableau de bord',
    sessions: 'Sessions',
    settings: 'Paramètres',
    profile: 'Profil',
    logout: 'Déconnexion',
    save: 'Enregistrer',
    cancel: 'Annuler',
    loading: 'Chargement...',
    // Date-related
    today: "Aujourd'hui",
    yesterday: 'Hier',
    tomorrow: 'Demain',
    // Navigation
    organizations: 'Organisations',
    users: 'Utilisateurs',
    analytics: 'Analytique',
    billing: 'Facturation',
    waitlist: "Liste d'attente",
    trainers: 'Formateurs',
    reports: 'Rapports',
    subscription: 'Abonnement',
    mySessions: 'Mes Sessions',
    workbooks: 'Cahiers',
    terminology: 'Terminologie',
    performance: 'Performance',
    help: 'Aide',
    // Actions
    create: 'Créer',
    edit: 'Modifier',
    delete: 'Supprimer',
    view: 'Voir',
    search: 'Rechercher',
    filter: 'Filtrer',
    export: 'Exporter',
    import: 'Importer',
    // Status
    active: 'Actif',
    inactive: 'Inactif',
    pending: 'En attente',
    completed: 'Terminé',
    scheduled: 'Planifié',
    // Dashboard cards
    totalSessions: 'Sessions Totales',
    totalParticipants: 'Participants Totaux',
    avgEngagement: 'Engagement Moyen',
    avgRating: 'Note Moyenne',
    // Reports
    sessionReports: 'Rapports de Session',
    trainerReports: 'Rapports des Formateurs',
  },
  de: {
    // Common
    dashboard: 'Dashboard',
    sessions: 'Sitzungen',
    settings: 'Einstellungen',
    profile: 'Profil',
    logout: 'Abmelden',
    save: 'Speichern',
    cancel: 'Abbrechen',
    loading: 'Wird geladen...',
    // Date-related
    today: 'Heute',
    yesterday: 'Gestern',
    tomorrow: 'Morgen',
    // Navigation
    organizations: 'Organisationen',
    users: 'Benutzer',
    analytics: 'Analytik',
    billing: 'Abrechnung',
    waitlist: 'Warteliste',
    trainers: 'Trainer',
    reports: 'Berichte',
    subscription: 'Abonnement',
    mySessions: 'Meine Sitzungen',
    workbooks: 'Arbeitsbücher',
    terminology: 'Terminologie',
    performance: 'Leistung',
    help: 'Hilfe',
    // Actions
    create: 'Erstellen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    view: 'Ansehen',
    search: 'Suchen',
    filter: 'Filtern',
    export: 'Exportieren',
    import: 'Importieren',
    // Status
    active: 'Aktiv',
    inactive: 'Inaktiv',
    pending: 'Ausstehend',
    completed: 'Abgeschlossen',
    scheduled: 'Geplant',
    // Dashboard cards
    totalSessions: 'Gesamte Sitzungen',
    totalParticipants: 'Gesamte Teilnehmer',
    avgEngagement: 'Durchschn. Engagement',
    avgRating: 'Durchschn. Bewertung',
    // Reports
    sessionReports: 'Sitzungsberichte',
    trainerReports: 'Trainerberichte',
  },
};

// Get browser's local timezone as default
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

export const PreferencesProvider = ({ children }) => {
  const [language, setLanguageState] = useState('en');
  const [timezone, setTimezoneState] = useState(getBrowserTimezone());
  const [dateFormat, setDateFormatState] = useState('MM/DD/YYYY');
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        // First check localStorage for immediate application
        const savedLanguage = localStorage.getItem('language');
        const savedTimezone = localStorage.getItem('timezone');
        const savedDateFormat = localStorage.getItem('dateFormat');

        if (savedLanguage) setLanguageState(savedLanguage);
        // Legacy: previous app default was 'UTC' which is wrong for India users
        // now that storage holds real UTC instants. Treat 'UTC' as "not set"
        // and fall back to the detected browser timezone.
        if (savedTimezone && savedTimezone !== 'UTC') {
          setTimezoneState(savedTimezone);
        } else if (savedTimezone === 'UTC') {
          localStorage.removeItem('timezone');
        }
        if (savedDateFormat) setDateFormatState(savedDateFormat);

        // If authenticated, fetch from API
        if (authService.isAuthenticated()) {
          const response = await authService.getSettings();
          if (response.success && response.data?.preferences) {
            const prefs = response.data.preferences;
            if (prefs.language) {
              setLanguageState(prefs.language);
              localStorage.setItem('language', prefs.language);
            }
            if (prefs.timezone && prefs.timezone !== 'UTC') {
              setTimezoneState(prefs.timezone);
              localStorage.setItem('timezone', prefs.timezone);
            }
            if (prefs.dateFormat) {
              setDateFormatState(prefs.dateFormat);
              localStorage.setItem('dateFormat', prefs.dateFormat);
            }
          }
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, []);

  // Set language
  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  }, []);

  // Set timezone
  const setTimezone = useCallback((tz) => {
    setTimezoneState(tz);
    localStorage.setItem('timezone', tz);
  }, []);

  // Set date format
  const setDateFormat = useCallback((format) => {
    setDateFormatState(format);
    localStorage.setItem('dateFormat', format);
  }, []);

  // Format date according to user preference
  // Uses UTC methods since dates are stored as pseudo-UTC
  const formatDate = useCallback((date, includeTime = false) => {
    if (!date) return '';

    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    let formatted = '';
    // Use UTC methods to avoid timezone shifting for pseudo-UTC stored dates
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();

    switch (dateFormat) {
      case 'DD/MM/YYYY':
        formatted = `${day}/${month}/${year}`;
        break;
      case 'YYYY-MM-DD':
        formatted = `${year}-${month}-${day}`;
        break;
      case 'MM/DD/YYYY':
      default:
        formatted = `${month}/${day}/${year}`;
        break;
    }

    if (includeTime) {
      const hours = String(d.getUTCHours()).padStart(2, '0');
      const minutes = String(d.getUTCMinutes()).padStart(2, '0');
      formatted += ` ${hours}:${minutes}`;
    }

    return formatted;
  }, [dateFormat]);

  // Format date with timezone consideration — "10 AUG 2026" project-wide format.
  // Second parameter can be either boolean (includeTime) or options object
  // (caller-supplied options take precedence over the standardized format).
  // Stored times are real UTC instants (Option B); display in user's timezone
  // (defaults to the browser's detected zone, typically Asia/Kolkata for India).
  const formatDateWithTimezone = useCallback((date, optionsOrIncludeTime = false) => {
    if (!date) return '';

    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';

      const tz = timezone || 'Asia/Kolkata';

      // Caller-supplied options branch — preserves backward-compat for any
      // callers that explicitly pass an options object expecting full control.
      if (typeof optionsOrIncludeTime === 'object' && optionsOrIncludeTime !== null) {
        const dateOptions = { timeZone: tz, ...optionsOrIncludeTime };
        return new Intl.DateTimeFormat('en-GB', dateOptions).format(d);
      }

      // Standard branch — emit "10 AUG 2026", optionally with time appended.
      const includeTime = optionsOrIncludeTime === true;
      const dateFormatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      // en-GB short month is "Aug"; uppercase the whole string is safe (only
      // letters in the month survive the operation).
      let formatted = dateFormatter.format(d).toUpperCase();

      if (includeTime) {
        const timeFormatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        formatted += `, ${timeFormatter.format(d)}`;
      }

      return formatted;
    } catch (error) {
      console.error('Error formatting date:', error);
      return formatDate(date, typeof optionsOrIncludeTime === 'boolean' ? optionsOrIncludeTime : false);
    }
  }, [formatDate, timezone]);

  // Format time only - 12-hour format with am/pm (lowercase, 2-digit hours)
  // Stored times are real UTC; display in user's timezone (defaults to IST).
  const formatTime = useCallback((date) => {
    if (!date) return '--:--';

    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '--:--';

      const tz = timezone || 'Asia/Kolkata';
      const options = {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      };

      const formatter = new Intl.DateTimeFormat('en-US', options);
      // Convert to lowercase am/pm
      return formatter.format(d).toLowerCase();
    } catch (error) {
      console.error('Error formatting time:', error);
      const d = new Date(date);
      let hours = d.getUTCHours();
      const minutes = String(d.getUTCMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    }
  }, [timezone]);

  // Format relative date (Today, Yesterday, etc.)
  // Uses UTC methods for pseudo-UTC stored dates
  const formatRelativeDate = useCallback((date) => {
    if (!date) return '';

    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Compare using UTC date components from stored date vs local date components for today/yesterday/tomorrow
    // This is because stored dates are pseudo-UTC but we want to compare with user's local "today"
    const isSameDay = (storedDate, localDate) =>
      storedDate.getUTCDate() === localDate.getDate() &&
      storedDate.getUTCMonth() === localDate.getMonth() &&
      storedDate.getUTCFullYear() === localDate.getFullYear();

    const t = translations[language] || translations.en;

    if (isSameDay(d, today)) return t.today;
    if (isSameDay(d, yesterday)) return t.yesterday;
    if (isSameDay(d, tomorrow)) return t.tomorrow;

    return formatDate(date);
  }, [language, formatDate]);

  // Translate function
  const t = useCallback((key) => {
    const langTranslations = translations[language] || translations.en;
    return langTranslations[key] || key;
  }, [language]);

  const value = {
    // State
    language,
    timezone,
    dateFormat,
    isLoading,

    // Setters
    setLanguage,
    setTimezone,
    setDateFormat,

    // Utilities
    formatDate,
    formatDateWithTimezone,
    formatTime,
    formatRelativeDate,
    t,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export default PreferencesContext;
